/**
 * MyPropertyTab — borrower-facing property surface (Issue #1623, Phase 2H).
 *
 * Renders a redacted view of the case's property evaluation for the
 * authenticated borrower. The shape is `PropertyEvaluationBorrowerView`
 * (defined in shared-types) — the underwriter-facing scoring fields
 * (verdict, confidence, factors, narrative, strategies, rehabLineItems,
 * negotiate/accept risk-flag buckets) are stripped server-side by the
 * `toBorrowerView` projection in 2-api-gateway.
 *
 * The borrower sees:
 *   1. AVM range card     — P10 / mid / P90 lender's estimated value range
 *                            and their stated purchase price.
 *   2. Comparables card   — recent sale comps (read-only); empty state
 *                            when none have been pulled yet.
 *   3. Market card        — ZIP-level market context (median sale price,
 *                            DOM, price change, active listings) when
 *                            available.
 *   4. Items-to-address   — borrower-actionable resolve flags
 *                            (`recommendedAction` starts with `Borrower:`).
 *
 * Forbidden in this surface (P0 — must not regress):
 *   - No verdict pill, no confidence score
 *   - No strategies (Fix-and-flip, Rental hold, DSCR, cap rate, MAO)
 *   - No rehab line-item table
 *   - No "Run full analysis" / "Run triage" CTAs (underwriter-only)
 *   - No raw narrative (server projection drops it; we don't need to
 *     re-redact, but we also don't surface it)
 *
 * The component does not call any mutation procedures — it is read-only
 * by design. tRPC `enabled` guard checks the `caseId` is a UUID before
 * the query runs, mirroring the backend Zod schema.
 */

'use client';

import {
  formatCurrencyWhole,
  GlassAlert,
  GlassCard,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type {
  PropertyComp,
  PropertyEvaluationBorrowerView,
  PropertyEvaluationRiskFlag,
  PropertyMarketSnapshot,
} from '@ops-matrix/shared-types';
import type { ReactElement } from 'react';
import { z } from 'zod';
import { trpc } from '../../lib/trpc/client';

// ============================================================================
// Types
// ============================================================================

export interface MyPropertyTabProps {
  caseId: string;
}

// ============================================================================
// Component
// ============================================================================

export function MyPropertyTab({ caseId }: MyPropertyTabProps): ReactElement {
  const { t } = useI18n();

  // Frontend Zod guard mirrors the backend schema (`z.string().uuid()`).
  // Without this, an invalid id triggers a 400 round-trip; the `enabled`
  // gate keeps the query from firing until we have a real case id.
  const isValidCaseId = z.string().uuid().safeParse(caseId).success;

  const { data, isLoading, error } = trpc.propertyEvaluation.borrower.getMyProperty.useQuery(
    { caseId },
    { enabled: isValidCaseId },
  );

  if (!isValidCaseId) {
    // Defensive — the parent route already validates the param, but if a
    // bad caseId ever reaches here, render nothing rather than a stale
    // payload from a previous query.
    return <Stack data-testid="my-property-tab-invalid" />;
  }

  if (isLoading) {
    return (
      <Stack spacing={2} data-testid="my-property-tab-loading">
        <GlassCard sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('common.loading')}
          </Typography>
        </GlassCard>
      </Stack>
    );
  }

  // NOT_FOUND = no evaluation has been run for this case yet. Render the
  // localized empty-state copy rather than a hard error — the borrower
  // can't kick off a triage themselves, so there's no CTA to surface.
  if (error && error.data?.code === 'NOT_FOUND') {
    return (
      <GlassCard sx={{ p: 2 }} data-testid="my-property-tab-empty">
        <Stack spacing={1}>
          <Typography variant="h6">{t('consumer.propertyAnalysis.tabLabel')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('consumer.propertyAnalysis.itemsToAddress.empty')}
          </Typography>
        </Stack>
      </GlassCard>
    );
  }

  if (error) {
    return (
      <GlassAlert severity="error" visible data-testid="my-property-tab-error">
        {t('consumer.propertyAnalysis.errors.loadFailed')}
      </GlassAlert>
    );
  }

  if (!data) {
    return <Stack data-testid="my-property-tab-loading" />;
  }

  return (
    <Stack spacing={3} data-testid="my-property-tab">
      <AvmRangeCard view={data} t={t} />
      <ComparablesCard comparables={data.comparables} t={t} />
      <MarketCard snapshot={data.marketSnapshot} t={t} />
      <ItemsToAddressCard flags={data.itemsToAddress} t={t} />
    </Stack>
  );
}

// ============================================================================
// AVM range card
// ============================================================================

interface AvmRangeCardProps {
  view: PropertyEvaluationBorrowerView;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/**
 * AVM range tile + purchase-price tile. The "estimated value range" tile
 * is two-line: the mid value on top, the P10–P90 band underneath as a
 * caption. We deliberately do not surface a confidence score or verdict
 * — that's underwriter-only.
 */
function AvmRangeCard({ view, t }: AvmRangeCardProps): ReactElement {
  const empty = '—';
  const mid = view.asIsValue != null ? formatCurrencyWhole(view.asIsValue) : empty;
  const low = view.asIsValueP10 != null ? formatCurrencyWhole(view.asIsValueP10) : null;
  const high = view.asIsValueP90 != null ? formatCurrencyWhole(view.asIsValueP90) : null;
  const range = low && high ? `${low} – ${high}` : null;
  const purchase = view.purchasePrice != null ? formatCurrencyWhole(view.purchasePrice) : empty;

  return (
    <GlassCard sx={{ p: 2 }} data-testid="my-property-avm-range">
      <Stack spacing={2}>
        <Typography variant="h6">{t('consumer.propertyAnalysis.avmRange.title')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {mid}
            </Typography>
            {range != null && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {range}
              </Typography>
            )}
          </Stack>
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              {t('consumer.propertyAnalysis.avmRange.purchasePrice')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {purchase}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </GlassCard>
  );
}

// ============================================================================
// Comparables card (sale comps only — borrower-facing simplification)
// ============================================================================

interface ComparablesCardProps {
  comparables: PropertyEvaluationBorrowerView['comparables'];
  t: (key: string) => string;
}

/**
 * Sale comparables — beds/baths/sqft/price/distance. Rent comps are
 * irrelevant to most borrower personas at this stage of the loan journey
 * (they're a refi/DSCR underwriting input), so the borrower sees only
 * sale comps; if/when a rental-loan-specific borrower surface is added
 * we can split into a tabbed view.
 */
function ComparablesCard({ comparables, t }: ComparablesCardProps): ReactElement {
  const sale: PropertyComp[] = comparables?.sale ?? [];

  return (
    <GlassCard sx={{ p: 2 }} data-testid="my-property-comparables">
      <Stack spacing={2}>
        <Typography variant="h6">{t('consumer.propertyAnalysis.comparables.title')}</Typography>
        {sale.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
            {t('consumer.propertyAnalysis.comparables.empty')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('consumer.propertyAnalysis.comparables.address')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('consumer.propertyAnalysis.comparables.bedsBaths')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('consumer.propertyAnalysis.comparables.sqft')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('consumer.propertyAnalysis.comparables.price')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('consumer.propertyAnalysis.comparables.distance')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sale.map((comp, idx) => (
                  <TableRow key={`${comp.address}-${idx}`}>
                    <TableCell>
                      <Typography variant="body2">{comp.address}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {comp.beds ?? '—'}/{comp.baths ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{comp.sqft != null ? comp.sqft.toLocaleString() : '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {comp.price != null ? formatCurrencyWhole(comp.price) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {comp.distanceMi != null ? `${comp.distanceMi.toFixed(1)} mi` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </GlassCard>
  );
}

// ============================================================================
// Market card (ZIP-level snapshot)
// ============================================================================

interface MarketCardProps {
  snapshot: PropertyMarketSnapshot | null;
  t: (key: string) => string;
}

/**
 * ZIP-level market context. Renders an empty-state when the snapshot is
 * null (rare — fringe markets where SFR Analytics has no ZIP-level data).
 * The same tiles as the underwriter view, but fewer of them and with
 * borrower-friendly labels (no "Median DOM" jargon).
 */
function MarketCard({ snapshot, t }: MarketCardProps): ReactElement {
  return (
    <GlassCard sx={{ p: 2 }} data-testid="my-property-market">
      <Stack spacing={2}>
        <Typography variant="h6">{t('consumer.propertyAnalysis.market.title')}</Typography>
        {snapshot == null ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
            {t('common.noData')}
          </Typography>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ flexWrap: 'wrap' }}>
            <MarketTile
              label={t('consumer.propertyAnalysis.market.medianSalePrice')}
              value={
                snapshot.medianSalePrice != null ? formatCurrencyWhole(snapshot.medianSalePrice) : t('common.noData')
              }
            />
            <MarketTile
              label={t('consumer.propertyAnalysis.market.medianDaysOnMarket')}
              value={snapshot.medianDaysOnMarket != null ? `${snapshot.medianDaysOnMarket}` : t('common.noData')}
            />
            <MarketTile
              label={t('consumer.propertyAnalysis.market.sixMonthChange')}
              value={
                snapshot.sixMonthPriceChangePct != null
                  ? formatPct(snapshot.sixMonthPriceChangePct)
                  : t('common.noData')
              }
            />
          </Stack>
        )}
      </Stack>
    </GlassCard>
  );
}

interface MarketTileProps {
  label: string;
  value: string;
}

function MarketTile({ label, value }: MarketTileProps): ReactElement {
  return (
    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 140 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Format a decimal fraction as a signed percentage (e.g. -0.08 → "-8.0%").
 */
function formatPct(value: number): string {
  const pct = value * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

// ============================================================================
// Items-to-address card
// ============================================================================

interface ItemsToAddressCardProps {
  flags: PropertyEvaluationRiskFlag[];
  t: (key: string) => string;
}

/**
 * Borrower-actionable resolve flags. The server projection only lets
 * `recommendedAction.startsWith('Borrower:')` flags through, so by
 * construction every item here is something the borrower can act on
 * (e.g. "Borrower: Email a renewed insurance binder").
 *
 * Each row shows:
 *   - flag.title (bold)
 *   - flag.impact (caption, e.g. "+$200/mo")
 *   - flag.recommendedAction (full sentence — kept verbatim from the
 *     server because it's the actionable instruction)
 */
function ItemsToAddressCard({ flags, t }: ItemsToAddressCardProps): ReactElement {
  return (
    <GlassCard sx={{ p: 2 }} data-testid="my-property-items">
      <Stack spacing={2}>
        <Typography variant="h6">{t('consumer.propertyAnalysis.itemsToAddress.title')}</Typography>
        {flags.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
            {t('consumer.propertyAnalysis.itemsToAddress.empty')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {flags.map((flag) => (
              <Stack key={flag.id} spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {flag.title}
                </Typography>
                {flag.impact ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {flag.impact}
                  </Typography>
                ) : null}
                {flag.recommendedAction ? <Typography variant="caption">{flag.recommendedAction}</Typography> : null}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </GlassCard>
  );
}
